/** All export workflows in one place. */
import { signal } from '@preact/signals';
import {
  exportAnnotatedPng,
  exportAreaCsv,
  exportChartPng,
  exportChartSvg,
  exportMetricsCsv,
  exportPlane,
  exportProfileCsv,
  exportReportJson,
  exportViewPng,
} from '../../app/exporters';
import { saveSession, loadSessionFile } from '../../app/session';
import { measurement, selectedLine } from '../../app/store';
import { Button, Field, NumberInput, Section, Toggle } from '../controls';

const chartScale = signal(2);
const chartW = signal(1200);
const chartH = signal(600);
export const embedImages = signal(true);

export function ExportPanel() {
  const hasData = !!measurement.value;
  const hasLine = !!selectedLine.value;
  return (
    <div class="export-panel">
      <Section title="Profile data" help="export.profile">
        <div class="btn-col">
          <Button
            icon="download"
            disabled={!hasLine}
            help="export.profileCsv"
            onClick={() => exportProfileCsv(false)}
            testId="export-profile-csv"
          >
            Selected profile (CSV)
          </Button>
          <Button icon="download" disabled={!hasLine} help="export.allProfiles" onClick={() => exportProfileCsv(true)}>
            All line profiles (CSV each)
          </Button>
          <Button icon="download" disabled={!hasLine} help="export.metricsCsv" onClick={exportMetricsCsv} testId="export-metrics-csv">
            Metrics summary (CSV)
          </Button>
          <Button icon="download" disabled={!hasData} help="export.areaCsv" onClick={exportAreaCsv}>
            Area / point ROI statistics (CSV)
          </Button>
          <Button icon="download" disabled={!hasData} help="export.report" onClick={exportReportJson} testId="export-report">
            Full report (JSON)
          </Button>
        </div>
      </Section>
      <Section title="Charts" help="export.chart">
        <div class="inline wrap">
          <Field label="Size" help="export.chartSize">
            <span class="inline">
              <NumberInput value={chartW.value} integer min={200} max={8000} onChange={(v) => (chartW.value = v)} />×
              <NumberInput value={chartH.value} integer min={150} max={8000} onChange={(v) => (chartH.value = v)} />
            </span>
          </Field>
          <Field label="PNG scale" help="export.chartScale">
            <NumberInput value={chartScale.value} min={1} max={6} step={1} integer onChange={(v) => (chartScale.value = v)} />
          </Field>
        </div>
        <div class="btn-col">
          <Button
            icon="chart"
            disabled={!hasLine}
            help="export.chartPng"
            onClick={() => exportChartPng(chartScale.value, chartW.value, chartH.value)}
            testId="export-chart-png"
          >
            Profile chart (PNG)
          </Button>
          <Button
            icon="chart"
            disabled={!hasLine}
            help="export.chartSvg"
            onClick={() => exportChartSvg(chartW.value, chartH.value)}
            testId="export-chart-svg"
          >
            Profile chart (SVG vector)
          </Button>
        </div>
      </Section>
      <Section title="Images" help="export.images">
        <div class="btn-col">
          <Button icon="image" disabled={!hasData} help="export.annotated" onClick={exportAnnotatedPng} testId="export-annotated">
            Annotated image, full resolution (PNG)
          </Button>
          <Button icon="image" disabled={!hasData} help="export.view" onClick={exportViewPng}>
            Current view (PNG)
          </Button>
          <Button icon="download" disabled={!hasData} help="export.tiff" onClick={() => exportPlane('tiff')} testId="export-tiff">
            Measurement plane (32-bit float TIFF)
          </Button>
          <Button icon="download" disabled={!hasData} help="export.pfm" onClick={() => exportPlane('pfm')}>
            Measurement plane (PFM)
          </Button>
        </div>
      </Section>
      <Section title="Session" help="export.session">
        <Toggle
          label="Embed images in session file"
          checked={embedImages.value}
          help="export.embed"
          onChange={(v) => (embedImages.value = v)}
        />
        <div class="btn-col">
          <Button icon="save" help="export.saveSession" onClick={() => saveSession(embedImages.value)} testId="save-session">
            Save session (.aperto)
          </Button>
          <Button icon="open" help="export.loadSession" onClick={loadSessionFile}>
            Load session…
          </Button>
        </div>
      </Section>
    </div>
  );
}
